import React, { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Search,
  Filter,
  SlidersHorizontal,
  X,
  ChevronDown,
  Star,
  Award,
  Truck,
  Grid3X3,
  List,
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Slider } from '../../components/ui/slider';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '../../components/ui/sheet';
import ProductCard from '../../components/common/ProductCard';
import { products, conditions, bodyParts, rehabStages, categories } from '../../data/mock';

const ShopPage = () => {
  const [searchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('best-match');
  const [viewMode, setViewMode] = useState('grid');
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);

  // Filters
  const [selectedConditions, setSelectedConditions] = useState([]);
  const [selectedBodyParts, setSelectedBodyParts] = useState([]);
  const [selectedStages, setSelectedStages] = useState([]);
  const [priceRange, setPriceRange] = useState([0, 100]);
  const [therapistOnly, setTherapistOnly] = useState(false);
  const [inStockOnly, setInStockOnly] = useState(false);
  const [fastShippingOnly, setFastShippingOnly] = useState(false);

  // Filter products
  const filteredProducts = useMemo(() => {
    let result = [...products];

    // Search query
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      result = result.filter(
        (p) =>
          p.name.toLowerCase().includes(query) ||
          p.shortDescription.toLowerCase().includes(query) ||
          p.conditions?.some((c) => c.includes(query))
      );
    }

    // Condition filter
    if (selectedConditions.length > 0) {
      result = result.filter((p) =>
        p.conditions?.some((c) => selectedConditions.includes(c))
      );
    }

    // Body part filter
    if (selectedBodyParts.length > 0) {
      result = result.filter((p) =>
        p.bodyParts?.some((b) => selectedBodyParts.includes(b))
      );
    }

    // Rehab stage filter
    if (selectedStages.length > 0) {
      result = result.filter((p) =>
        p.rehabStage?.some((s) => selectedStages.includes(s))
      );
    }

    // Price filter
    result = result.filter((p) => p.price >= priceRange[0] && p.price <= priceRange[1]);

    // Therapist recommended
    if (therapistOnly) {
      result = result.filter((p) => p.badges?.includes('therapist-recommended'));
    }

    // In stock
    if (inStockOnly) {
      result = result.filter((p) => p.stock > 0);
    }

    // Fast shipping
    if (fastShippingOnly) {
      result = result.filter((p) => p.fastShipping);
    }

    // Sort
    switch (sortBy) {
      case 'price-low':
        result.sort((a, b) => a.price - b.price);
        break;
      case 'price-high':
        result.sort((a, b) => b.price - a.price);
        break;
      case 'rating':
        result.sort((a, b) => b.rating - a.rating);
        break;
      case 'best-seller':
        result.sort((a, b) => b.reviewCount - a.reviewCount);
        break;
      default:
        // best-match - keep original order
        break;
    }

    return result;
  }, [
    searchQuery,
    selectedConditions,
    selectedBodyParts,
    selectedStages,
    priceRange,
    therapistOnly,
    inStockOnly,
    fastShippingOnly,
    sortBy,
  ]);

  const clearAllFilters = () => {
    setSelectedConditions([]);
    setSelectedBodyParts([]);
    setSelectedStages([]);
    setPriceRange([0, 100]);
    setTherapistOnly(false);
    setInStockOnly(false);
    setFastShippingOnly(false);
  };

  const activeFilterCount =
    selectedConditions.length +
    selectedBodyParts.length +
    selectedStages.length +
    (therapistOnly ? 1 : 0) +
    (inStockOnly ? 1 : 0) +
    (fastShippingOnly ? 1 : 0) +
    (priceRange[0] > 0 || priceRange[1] < 100 ? 1 : 0);

  const FilterSection = ({ title, children }) => (
    <div className="py-4 border-b border-gray-100">
      <h3 className="text-sm font-semibold text-gray-900 mb-3">{title}</h3>
      {children}
    </div>
  );

  const FilterCheckbox = ({ checked, onChange, label, count }) => (
    <label className="flex items-center gap-2 py-1.5 cursor-pointer group">
      <Checkbox
        checked={checked}
        onCheckedChange={onChange}
        className="data-[state=checked]:bg-red-600 data-[state=checked]:border-red-600"
      />
      <span className="text-sm text-gray-600 group-hover:text-gray-900 flex-1">{label}</span>
      {count !== undefined && (
        <span className="text-xs text-gray-400">({count})</span>
      )}
    </label>
  );

  const FiltersContent = () => (
    <div className="space-y-0">
      {/* Quick Filters */}
      <FilterSection title="Quick Filters">
        <div className="space-y-1">
          <FilterCheckbox
            checked={therapistOnly}
            onChange={setTherapistOnly}
            label="Therapist Recommended"
          />
          <FilterCheckbox
            checked={inStockOnly}
            onChange={setInStockOnly}
            label="In Stock"
          />
          <FilterCheckbox
            checked={fastShippingOnly}
            onChange={setFastShippingOnly}
            label="Fast Shipping"
          />
        </div>
      </FilterSection>

      {/* Condition */}
      <FilterSection title="Condition">
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {conditions.map((condition) => (
            <FilterCheckbox
              key={condition.id}
              checked={selectedConditions.includes(condition.id)}
              onChange={(checked) => {
                setSelectedConditions((prev) =>
                  checked ? [...prev, condition.id] : prev.filter((c) => c !== condition.id)
                );
              }}
              label={condition.name}
              count={products.filter((p) => p.conditions?.includes(condition.id)).length}
            />
          ))}
        </div>
      </FilterSection>

      {/* Body Part */}
      <FilterSection title="Body Part">
        <div className="space-y-1 max-h-48 overflow-y-auto">
          {bodyParts.map((part) => (
            <FilterCheckbox
              key={part.id}
              checked={selectedBodyParts.includes(part.id)}
              onChange={(checked) => {
                setSelectedBodyParts((prev) =>
                  checked ? [...prev, part.id] : prev.filter((b) => b !== part.id)
                );
              }}
              label={part.name}
            />
          ))}
        </div>
      </FilterSection>

      {/* Rehab Stage */}
      <FilterSection title="Rehab Stage">
        <div className="space-y-1">
          {rehabStages.map((stage) => (
            <FilterCheckbox
              key={stage.id}
              checked={selectedStages.includes(stage.id)}
              onChange={(checked) => {
                setSelectedStages((prev) =>
                  checked ? [...prev, stage.id] : prev.filter((s) => s !== stage.id)
                );
              }}
              label={stage.name}
            />
          ))}
        </div>
      </FilterSection>

      {/* Price Range */}
      <FilterSection title="Price Range">
        <div className="px-2">
          <Slider
            value={priceRange}
            onValueChange={setPriceRange}
            max={100}
            step={5}
            className="my-4"
          />
          <div className="flex items-center justify-between text-sm text-gray-600">
            <span>${priceRange[0]}</span>
            <span>${priceRange[1]}+</span>
          </div>
        </div>
      </FilterSection>

      {activeFilterCount > 0 && (
        <div className="pt-4">
          <Button
            variant="outline"
            onClick={clearAllFilters}
            className="w-full border-gray-200 text-gray-600 hover:bg-gray-50"
          >
            Clear All Filters
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-100 sticky top-16 md:top-16 z-30">
        <div className="max-w-7xl mx-auto px-4 py-4">
          {/* Search Bar */}
          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search products, conditions, body parts..."
                className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-red-500/20 focus:border-red-500 transition-all"
              />
            </div>

            {/* Mobile Filter Button */}
            <Sheet open={mobileFiltersOpen} onOpenChange={setMobileFiltersOpen}>
              <SheetTrigger asChild>
                <Button
                  variant="outline"
                  className="md:hidden relative border-gray-200"
                >
                  <SlidersHorizontal className="w-4 h-4" />
                  {activeFilterCount > 0 && (
                    <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-600 text-white text-xs rounded-full flex items-center justify-center">
                      {activeFilterCount}
                    </span>
                  )}
                </Button>
              </SheetTrigger>
              <SheetContent side="left" className="w-80 p-0">
                <SheetHeader className="p-4 border-b border-gray-100">
                  <SheetTitle>Filters</SheetTitle>
                </SheetHeader>
                <div className="overflow-y-auto h-[calc(100vh-80px)] px-4">
                  <FiltersContent />
                </div>
              </SheetContent>
            </Sheet>
          </div>

          {/* Sort & View Controls */}
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">
              <span className="font-medium text-gray-900">{filteredProducts.length}</span> products found
            </p>
            <div className="flex items-center gap-3">
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="w-[160px] h-9 text-sm border-gray-200">
                  <SelectValue placeholder="Sort by" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="best-match">Best Match</SelectItem>
                  <SelectItem value="price-low">Price: Low to High</SelectItem>
                  <SelectItem value="price-high">Price: High to Low</SelectItem>
                  <SelectItem value="rating">Top Rated</SelectItem>
                  <SelectItem value="best-seller">Best Sellers</SelectItem>
                </SelectContent>
              </Select>

              <div className="hidden md:flex items-center gap-1 border border-gray-200 rounded-lg p-1">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-1.5 rounded ${
                    viewMode === 'grid' ? 'bg-gray-100 text-gray-900' : 'text-gray-400'
                  }`}
                >
                  <Grid3X3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-1.5 rounded ${
                    viewMode === 'list' ? 'bg-gray-100 text-gray-900' : 'text-gray-400'
                  }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>

          {/* Active Filters Pills */}
          {activeFilterCount > 0 && (
            <div className="flex items-center gap-2 mt-3 flex-wrap">
              {therapistOnly && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-red-50 text-red-700 text-sm rounded-full">
                  <Award className="w-3 h-3" />
                  Therapist Recommended
                  <button onClick={() => setTherapistOnly(false)}>
                    <X className="w-3 h-3 ml-1" />
                  </button>
                </span>
              )}
              {fastShippingOnly && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-cyan-50 text-cyan-700 text-sm rounded-full">
                  <Truck className="w-3 h-3" />
                  Fast Shipping
                  <button onClick={() => setFastShippingOnly(false)}>
                    <X className="w-3 h-3 ml-1" />
                  </button>
                </span>
              )}
              {selectedConditions.map((c) => (
                <span
                  key={c}
                  className="inline-flex items-center gap-1 px-3 py-1 bg-gray-100 text-gray-700 text-sm rounded-full"
                >
                  {conditions.find((cond) => cond.id === c)?.name}
                  <button
                    onClick={() =>
                      setSelectedConditions((prev) => prev.filter((id) => id !== c))
                    }
                  >
                    <X className="w-3 h-3 ml-1" />
                  </button>
                </span>
              ))}
              <button
                onClick={clearAllFilters}
                className="text-sm text-red-600 hover:underline"
              >
                Clear all
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Desktop Sidebar */}
          <aside className="hidden md:block w-64 flex-shrink-0">
            <Card className="sticky top-36 border-gray-100">
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-semibold text-gray-900">Filters</h2>
                  {activeFilterCount > 0 && (
                    <span className="text-xs font-medium text-red-600">
                      {activeFilterCount} active
                    </span>
                  )}
                </div>
                <FiltersContent />
              </CardContent>
            </Card>
          </aside>

          {/* Product Grid */}
          <main className="flex-1">
            {filteredProducts.length === 0 ? (
              <div className="text-center py-16">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Search className="w-8 h-8 text-gray-400" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No products found</h3>
                <p className="text-gray-500 mb-4">Try adjusting your filters or search query</p>
                <Button variant="outline" onClick={clearAllFilters}>
                  Clear All Filters
                </Button>
              </div>
            ) : (
              <div
                className={`grid gap-4 md:gap-6 ${
                  viewMode === 'grid'
                    ? 'grid-cols-2 md:grid-cols-3'
                    : 'grid-cols-1'
                }`}
              >
                {filteredProducts.map((product) => (
                  <ProductCard key={product.id} product={product} />
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* Footer Spacer for mobile nav */}
      <div className="h-20 md:h-0" />
    </div>
  );
};

export default ShopPage;